/**
 * Service Report Printing Utility
 * Generates formatted HTML for printing service reports to PDF
 * Follows the same pattern as inventoryReportPrint.js
 */
import { showAlert } from "@/plugins/alertDialog";

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return (
    "P " +
    num.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return Number(num).toLocaleString("en-PH");
};

const getBaseStyles = () => `
  @media print {
    @page {
      margin: 0.3in 0.4in;
      size: landscape;
    }
    body { margin: 0; }
    .no-print { display: none !important; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    padding: 12px 16px;
    color: #2c3e50;
    font-size: 9px;
    line-height: 1.3;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 6px;
    border-bottom: 3px double #1976d2;
    margin-bottom: 8px;
  }
  .company-name { font-size: 18px; font-weight: bold; letter-spacing: 1.5px; }
  .company-name .red { color: #d32f2f; }
  .company-name .blue { color: #1976d2; }
  .company-tagline { font-size: 8px; color: #555; font-weight: 600; }
  .company-details { font-size: 7px; color: #777; margin-top: 1px; }
  .doc-title { font-size: 16px; font-weight: 700; color: #1976d2; letter-spacing: 1px; text-align: right; }
  .doc-subtitle { font-size: 9px; color: #555; text-align: right; margin-top: 2px; }

  .filters-bar {
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    padding: 4px 10px;
    margin-bottom: 8px;
    font-size: 8px;
    color: #555;
  }
  .filters-bar span { margin-right: 15px; }
  .filters-bar strong { color: #333; }

  .summary-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .summary-card {
    flex: 1;
    border: 1px solid #ccc;
    padding: 6px 10px;
    text-align: center;
  }
  .summary-card .value { font-size: 13px; font-weight: 700; }
  .summary-card .label { font-size: 7px; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }

  .section-title {
    font-size: 11px;
    font-weight: 700;
    color: #1976d2;
    margin: 10px 0 4px 0;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 2px;
  }

  table.report-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
  }
  table.report-table th {
    background: #1976d2;
    color: white;
    padding: 4px 5px;
    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-align: left;
    white-space: nowrap;
  }
  table.report-table td {
    border: 1px solid #e0e0e0;
    padding: 3px 5px;
    font-size: 9px;
  }
  table.report-table tbody tr:nth-child(even) { background: #f9f9f9; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }

  .footer {
    margin-top: 10px;
    padding-top: 4px;
    border-top: 1px solid #ddd;
    text-align: center;
    color: #aaa;
    font-size: 7px;
  }
`;

const generateHeader = (reportTitle) => `
  <div class="header">
    <div>
      <div class="company-name">
        <span class="red">JMB</span> <span class="blue">COMPUTERS</span>
      </div>
      <div class="company-tagline">JMB COMPUTER AND LAPTOP REPAIR SERVICES</div>
      <div class="company-details">VAT REG | TIN: 703-730-173 | PhilGEPS Registered Platinum</div>
      <div class="company-details">Main: P2, Magosilom, Cantilan, Surigao del Sur | Tandag: P Villa Bali, Mabua, Tandag City</div>
    </div>
    <div>
      <div class="doc-title">${reportTitle}</div>
      <div class="doc-subtitle">Service Report</div>
    </div>
  </div>
`;

const generateFiltersBar = (filters) => {
  const parts = [];
  if (filters.from_date)
    parts.push(`<span><strong>From:</strong> ${filters.from_date}</span>`);
  if (filters.to_date)
    parts.push(`<span><strong>To:</strong> ${filters.to_date}</span>`);
  if (parts.length === 0) return "";
  return `<div class="filters-bar">${parts.join("")}</div>`;
};

const generateSummaryCards = (cards) => {
  if (!cards || cards.length === 0) return "";
  return `<div class="summary-row">${cards
    .map(
      (c) =>
        `<div class="summary-card"><div class="value">${c.value}</div><div class="label">${c.label}</div></div>`
    )
    .join("")}</div>`;
};

const generateTable = (headers, rows) => {
  if (!rows || rows.length === 0) {
    return `<p style="text-align:center;color:#999;padding:20px;">No data available.</p>`;
  }
  let html = `<table class="report-table"><thead><tr>`;
  headers.forEach((h) => {
    const align = h.align ? ` style="text-align:${h.align};"` : "";
    html += `<th${align}>${h.text}</th>`;
  });
  html += `</tr></thead><tbody>`;
  rows.forEach((row) => {
    html += `<tr>`;
    headers.forEach((h) => {
      const align = h.align ? ` style="text-align:${h.align};"` : "";
      const cls = h.bold ? ' class="bold"' : "";
      html += `<td${align}${cls}>${
        row[h.key] !== undefined && row[h.key] !== null ? row[h.key] : "-"
      }</td>`;
    });
    html += `</tr>`;
  });
  html += `</tbody></table>`;
  return html;
};

const generateFooter = () =>
  `<div class="footer">Generated on ${new Date().toLocaleString()} | JMB Computer and Laptop Repair Services</div>`;

const wrapHTML = (title, bodyContent) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>${getBaseStyles()}</style>
</head>
<body>${bodyContent}</body>
</html>`;

// --- Report Generators ---

const generateRevenueSummaryHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Service Types", value: report.total_service_types },
    {
      label: "Total Revenue",
      value: formatCurrency(report.grand_total_revenue),
    },
    {
      label: "Times Rendered",
      value: formatNumber(report.grand_total_times_rendered),
    },
    { label: "Period", value: report.from_date + " - " + report.to_date },
  ]);
  const headers = [
    { text: "#", key: "rank", align: "center" },
    { text: "Service Name", key: "service_name" },
    { text: "Base Price", key: "base_price", align: "right" },
    { text: "Times Rendered", key: "times_rendered", align: "center" },
    { text: "Total Qty", key: "total_qty", align: "center" },
    { text: "Total Revenue", key: "total_revenue", align: "right" },
    {
      text: "Avg Revenue/Invoice",
      key: "avg_revenue_per_invoice",
      align: "right",
    },
    { text: "Profit Margin", key: "profit_margin", align: "center" },
  ];
  const rows = (report.items || []).map((i, idx) => ({
    ...i,
    rank: idx + 1,
    base_price: formatCurrency(i.base_price),
    total_revenue: formatCurrency(i.total_revenue),
    avg_revenue_per_invoice: formatCurrency(i.avg_revenue_per_invoice),
    profit_margin: i.profit_margin + "%",
  }));
  const body =
    generateHeader("SERVICE REVENUE SUMMARY") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Service Revenue Summary", body);
};

const generateTechnicianPerformanceHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Technicians", value: report.total_technicians },
    {
      label: "Total Problems",
      value: formatNumber(report.grand_total_problems),
    },
    { label: "Completed", value: formatNumber(report.grand_total_completed) },
    { label: "Pending", value: formatNumber(report.grand_total_pending) },
  ]);
  const headers = [
    { text: "Technician", key: "technician" },
    { text: "Total Problems", key: "total_problems", align: "center" },
    { text: "Completed", key: "completed", align: "center" },
    { text: "Pending", key: "pending", align: "center" },
    { text: "Completion Rate", key: "completion_rate", align: "center" },
    {
      text: "Avg Turnaround (hrs)",
      key: "avg_turnaround_hours",
      align: "center",
    },
    {
      text: "Avg Turnaround (days)",
      key: "avg_turnaround_days",
      align: "center",
    },
  ];
  const rows = (report.items || []).map((i) => ({
    ...i,
    completion_rate: i.completion_rate + "%",
    avg_turnaround_hours:
      i.avg_turnaround_hours !== null ? i.avg_turnaround_hours + " hrs" : "N/A",
    avg_turnaround_days:
      i.avg_turnaround_days !== null ? i.avg_turnaround_days + " days" : "N/A",
  }));
  const body =
    generateHeader("TECHNICIAN PERFORMANCE") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Technician Performance", body);
};

const generateTurnaroundTimeHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Overall Avg Days", value: report.overall_avg_days },
    { label: "Total Completed", value: formatNumber(report.total_completed) },
    {
      label: "Still In Progress",
      value: formatNumber(report.total_in_progress),
    },
    { label: "Period", value: report.from_date + " - " + report.to_date },
  ]);

  // Completed table
  const completedHeaders = [
    { text: "Unit Type", key: "unit_type" },
    { text: "Total Units", key: "total_units", align: "center" },
    { text: "Avg Days", key: "avg_days", align: "center" },
    { text: "Min Days", key: "min_days", align: "center" },
    { text: "Max Days", key: "max_days", align: "center" },
    { text: "Same Day", key: "same_day", align: "center" },
    { text: "1-3 Days", key: "days_1_3", align: "center" },
    { text: "4-7 Days", key: "days_4_7", align: "center" },
    { text: "7+ Days", key: "days_7_plus", align: "center" },
  ];

  // In-progress table
  const inProgressHeaders = [
    { text: "Unit Type", key: "unit_type" },
    { text: "Units Waiting", key: "units_waiting", align: "center" },
    { text: "Avg Days Waiting", key: "avg_days_waiting", align: "center" },
  ];

  const inProgressRows = (report.in_progress || []).map((i) => ({
    ...i,
    avg_days_waiting: i.avg_days_waiting + " days",
  }));

  const body =
    generateHeader("SERVICE TURNAROUND TIME") +
    generateFiltersBar(filters) +
    summary +
    `<div class="section-title">Completed Units</div>` +
    generateTable(completedHeaders, report.completed || []) +
    `<div class="section-title">In Progress</div>` +
    generateTable(inProgressHeaders, inProgressRows) +
    generateFooter();
  return wrapHTML("Service Turnaround Time", body);
};

const generateMonthlyTrendsHTML = (report, filters) => {
  const summary = generateSummaryCards([
    { label: "Total Months", value: report.total_months },
    {
      label: "Grand Total Revenue",
      value: formatCurrency(report.grand_total_revenue),
    },
    {
      label: "Total Services Rendered",
      value: formatNumber(report.grand_total_services_rendered),
    },
    { label: "Period", value: report.from_date + " - " + report.to_date },
  ]);
  const headers = [
    { text: "Month", key: "month" },
    { text: "Services Rendered", key: "services_rendered", align: "center" },
    { text: "Total Qty", key: "total_qty", align: "center" },
    { text: "Revenue", key: "revenue", align: "right" },
    { text: "Unique Customers", key: "unique_customers", align: "center" },
    { text: "Unique Services", key: "unique_services", align: "center" },
    { text: "Revenue Growth", key: "revenue_growth", align: "center" },
    { text: "Count Growth", key: "count_growth", align: "center" },
  ];
  const rows = (report.items || []).map((i) => ({
    ...i,
    revenue: formatCurrency(i.revenue),
    revenue_growth:
      i.revenue_growth !== null
        ? (i.revenue_growth >= 0 ? "+" : "") + i.revenue_growth + "%"
        : "N/A",
    count_growth:
      i.count_growth !== null
        ? (i.count_growth >= 0 ? "+" : "") + i.count_growth + "%"
        : "N/A",
  }));
  const body =
    generateHeader("MONTHLY SERVICE TRENDS") +
    generateFiltersBar(filters) +
    summary +
    generateTable(headers, rows) +
    generateFooter();
  return wrapHTML("Monthly Service Trends", body);
};

// --- Dispatcher ---

const generators = {
  revenue_summary: generateRevenueSummaryHTML,
  technician_performance: generateTechnicianPerformanceHTML,
  turnaround_time: generateTurnaroundTimeHTML,
  monthly_trends: generateMonthlyTrendsHTML,
};

const printReport = (html) => {
  const printWindow = window.open("", "", "width=1100,height=700");
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

export const printServiceReport = (reportKey, reportData, filters) => {
  const generator = generators[reportKey];
  if (!generator) {
    showAlert({
      title: "Error",
      message: "Unknown report type: " + reportKey,
      color: "error",
      icon: "mdi-alert-circle",
    });
    return;
  }
  const html = generator(reportData, filters || {});
  printReport(html);
};
